/**
 * Human wording for a streamed `tool_result` event.
 *
 * The stream used to print `<tool> completed` / `<tool> failed` from the
 * result's `success` flag alone. That is wrong for tools whose `success`
 * means "nothing left to fix" rather than "ran fine": validate_work sets
 * success=false whenever a single node is flagged, so "5 approved · 2
 * flagged" rendered as "validate_work failed" and read as a crash.
 *
 * Three sources, in order:
 *  1. `data.outcome` — a short line the backend attaches (newer backends;
 *     see `emit_tool_result(..., outcome=)`). Always wins.
 *  2. For validate_work only, the review summary every backend version has
 *     always sent as `data.result_summary` ("… 0 rejected, 3 flagged, 4
 *     approved …") — the counts are rendered from it, so an older backend
 *     still gets a truthful line instead of "failed".
 *  3. null — nothing more specific to say; callers fall back to the
 *     generic completed/failed wording.
 */

const REVIEW_COUNTS = /(\d+)\s+rejected,\s*(\d+)\s+flagged,\s*(\d+)\s+approved/i;

function readString(data: unknown, key: string): string | null {
  if (!data || typeof data !== 'object') return null;
  const value = (data as Record<string, unknown>)[key];
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/** Counts line from a validate_work review summary, or null if it has none. */
export function validateWorkOutcomeFromSummary(summary: string | null): string | null {
  if (!summary) return null;
  const match = REVIEW_COUNTS.exec(summary);
  if (!match) return null;
  const rejected = Number(match[1]);
  const flagged = Number(match[2]);
  const approved = Number(match[3]);
  // Counts only. The summary cannot tell an abandoned run from a partial
  // one (the "did not complete or approve" gate-seal sentence is appended
  // to every not-fully-approved quick run), so that distinction is left to
  // the backend `outcome` field, which newer builds always send.
  const parts = [`${approved} approved`];
  if (flagged) parts.push(`${flagged} flagged`);
  if (rejected) parts.push(`${rejected} rejected`);
  return parts.join(' · ');
}

export function describeToolOutcome(
  toolName: string,
  data: unknown,
): string | null {
  const outcome = readString(data, 'outcome');
  if (outcome) return `${toolName}: ${outcome}`;
  if (toolName === 'validate_work') {
    const fromSummary = validateWorkOutcomeFromSummary(readString(data, 'result_summary'));
    if (fromSummary) return `${toolName}: ${fromSummary}`;
  }
  return null;
}
